import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraSelect, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { projectDataClient } from '../../../lib/connect/clients';
import { TextField } from '../PastPerformance/Fields';
import { BlackHatEvidence } from './BlackHatEvidence';
import { BlackHatScenarioFacts, BlackHatScenarios } from './BlackHatScenarios';
import { scenarioDrafts, scenarioInputs } from './blackHatScenarioDrafts';
export const contentFields = {
    expectedStrategy: 'Expected strategy',
    strengths: 'Strengths',
    weaknesses: 'Weaknesses',
    likelyApproach: 'Likely approach',
    ourResponse: 'Our response',
    unknowns: 'Unknowns'
};
const contentOf = (head)=>Object.fromEntries(Object.keys(contentFields).map((key)=>[
            key,
            head?.content?.[key] ?? ''
        ]));
export function BlackHatFacts({ assessment }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "min-w-0 space-y-2",
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                className: "[overflow-wrap:anywhere]",
                children: [
                    "Version ",
                    assessment.version,
                    " · ",
                    assessment.createdBy,
                    " · ",
                    assessment.createdAt
                ]
            }),
            Object.entries(contentFields).map(([key, label])=>/*#__PURE__*/ _jsxs(SpecraText, {
                    as: "p",
                    className: "whitespace-pre-wrap [overflow-wrap:anywhere]",
                    children: [
                        label,
                        ": ",
                        assessment.content?.[key] || 'Not recorded'
                    ]
                }, key)),
            /*#__PURE__*/ _jsx(BlackHatScenarioFacts, {
                scenarios: assessment.scenarios
            }),
            !assessment.evidenceRefs.length && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                className: "[overflow-wrap:anywhere]",
                children: "No evidence attached."
            }),
            assessment.evidenceRefs.map((e)=>/*#__PURE__*/ _jsx(BlackHatEvidence, {
                    evidence: e
                }, e.evidenceId))
        ]
    });
}
export function BlackHatEditor({ pursuitId, participantId, head, evidence, onSaved, onDenied }) {
    const cache = useQueryClient();
    // In-memory query cache retains unsaved work across route/back navigation; no browser storage.
    const draftKey = [
        'black-hat-draft',
        pursuitId,
        participantId
    ];
    useQuery({
        queryKey: draftKey,
        queryFn: skipToken,
        enabled: false,
        gcTime: Number.POSITIVE_INFINITY
    });
    const [draft, setDraft] = useState(()=>cache.getQueryData(draftKey) ?? {
            content: contentOf(head),
            evidenceIds: head?.evidenceRefs.map((e)=>e.evidenceId) ?? [],
            scenarios: scenarioDrafts(head?.scenarios ?? []),
            version: head?.version ?? 0,
            conflict: false
        });
    const [busy, setBusy] = useState(false);
    const flight = useRef(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');
    const [latest, setLatest] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const dirty = JSON.stringify(draft.content) !== JSON.stringify(contentOf(head)) || JSON.stringify(draft.evidenceIds) !== JSON.stringify(head?.evidenceRefs.map((e)=>e.evidenceId) ?? []) || JSON.stringify(draft.scenarios) !== JSON.stringify(scenarioDrafts(head?.scenarios ?? []));
    useEffect(()=>{
        const warn = (event)=>{
            if (dirty) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', warn);
        return ()=>window.removeEventListener('beforeunload', warn);
    }, [
        dirty
    ]);
    function change(next) {
        setDraft(next);
        cache.setQueryData(draftKey, next);
        setSaved('');
    }
    async function save() {
        if (flight.current || draft.conflict || !Object.values(draft.content).some((v)=>v.trim())) return;
        flight.current = true;
        setBusy(true);
        setError('');
        setSaved('');
        try {
            const response = await projectDataClient.saveBlackHatAssessmentVersion({
                pursuitId,
                participantId,
                expectedVersion: draft.version,
                content: draft.content,
                evidenceIds: draft.evidenceIds,
                scenarios: scenarioInputs(draft.scenarios)
            });
            if (!response.assessment) throw new Error('Missing saved assessment');
            change({
                ...draft,
                content: contentOf(response.assessment),
                evidenceIds: response.assessment.evidenceRefs.map((e)=>e.evidenceId),
                scenarios: scenarioDrafts(response.assessment.scenarios),
                version: response.assessment.version
            });
            onSaved(response.assessment);
            setSaved(`Saved version ${response.assessment.version}`);
        } catch (cause) {
            const code = ConnectError.from(cause).code;
            if (code === Code.PermissionDenied || code === Code.Unauthenticated) onDenied();
            if (code === Code.Aborted) {
                change({
                    ...draft,
                    conflict: true
                });
                setLoaded(false);
                setLatest(null);
                setError('A newer version exists. Your draft is retained. Load latest, compare, and reconcile before saving.');
            } else setError(code === Code.InvalidArgument ? `Scenario validation failed: ${ConnectError.from(cause).rawMessage}. Your draft is retained.` : cause instanceof Error && !(cause instanceof ConnectError) ? cause.message : code === Code.NotFound ? 'Competitor is unavailable. Your draft is retained; return to Competition.' : 'Save failed. Your draft is retained. Retry saving when access or connection is restored.');
        } finally{
            flight.current = false;
            setBusy(false);
        }
    }
    async function loadLatest() {
        if (flight.current) return;
        flight.current = true;
        setBusy(true);
        setError('');
        try {
            const response = await projectDataClient.listBlackHatAssessments({
                pursuitId
            });
            setLatest(response.assessments.find((a)=>a.participantId === participantId) ?? null);
            setLoaded(true);
        } catch (cause) {
            if ([
                Code.PermissionDenied,
                Code.Unauthenticated
            ].includes(ConnectError.from(cause).code)) onDenied();
            setError('Latest version could not be loaded. Your draft is retained. Retry loading latest.');
        } finally{
            flight.current = false;
            setBusy(false);
        }
    }
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": Testids.blackHat.editor,
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsxs(SpecraText, {
                as: "p",
                className: "[overflow-wrap:anywhere]",
                children: [
                    "Editing version ",
                    draft.version || 'new',
                    ". Save appends a version. Unsaved drafts stay here when switching competitors, history or pages; save before closing this browser session."
                ]
            }),
            /*#__PURE__*/ _jsxs("fieldset", {
                disabled: busy,
                className: "space-y-3",
                children: [
                    Object.entries(contentFields).map(([key, label])=>/*#__PURE__*/ _jsx(TextField, {
                            label: label,
                            multiline: true,
                            testId: Testids.blackHat[key],
                            value: draft.content[key],
                            onChange: (value)=>change({
                                    ...draft,
                                    content: {
                                        ...draft.content,
                                        [key]: value
                                    }
                                })
                        }, key)),
                    /*#__PURE__*/ _jsx(BlackHatScenarios, {
                        scenarios: draft.scenarios,
                        onChange: (scenarios)=>change({
                                ...draft,
                                scenarios
                            })
                    }),
                    !!head?.scenarios.length && /*#__PURE__*/ _jsxs("section", {
                        "aria-label": "Saved scenario calculation",
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "p",
                                children: [
                                    "Saved calculation · version ",
                                    head.version,
                                    ". Save edits to recalculate."
                                ]
                            }),
                            /*#__PURE__*/ _jsx(BlackHatScenarioFacts, {
                                scenarios: head.scenarios
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "aria-label": "Attach existing competitor evidence",
                        "data-testid": Testids.blackHat.evidenceSelect,
                        value: "",
                        onChange: (event)=>{
                            const id = event.target.value;
                            if (id && !draft.evidenceIds.includes(id)) change({
                                ...draft,
                                evidenceIds: [
                                    ...draft.evidenceIds,
                                    id
                                ]
                            });
                        },
                        disabled: draft.evidenceIds.length >= 100,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Attach existing competitor evidence"
                            }),
                            evidence.filter((e)=>!draft.evidenceIds.includes(e.id)).map((e)=>/*#__PURE__*/ _jsxs("option", {
                                    value: e.id,
                                    children: [
                                        e.sourceRef || e.rationale || e.id,
                                        " · ",
                                        e.basis
                                    ]
                                }, e.id))
                        ]
                    }),
                    !evidence.length && /*#__PURE__*/ _jsx(SpecraText, {
                        as: "p",
                        className: "[overflow-wrap:anywhere]",
                        children: "No competitor evidence available. Evidence can be added in Competition; a partial assessment can still be saved."
                    }),
                    draft.evidenceIds.map((id)=>{
                        const e = evidence.find((item)=>item.id === id);
                        const frozen = head?.evidenceRefs.find((item)=>item.evidenceId === id);
                        return /*#__PURE__*/ _jsxs("div", {
                            children: [
                                frozen ? /*#__PURE__*/ _jsx(BlackHatEvidence, {
                                    evidence: frozen
                                }) : e ? /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "div",
                                    className: "space-y-1 [overflow-wrap:anywhere]",
                                    children: [
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Source: ",
                                                e.sourceRef || 'Not recorded'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Date: ",
                                                e.sourceDate?.toDate().toLocaleDateString() || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Basis: ",
                                                e.basis === 'assumption' ? 'Assumption — not verified' : e.basis
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Contributor: ",
                                                e.actorUserId || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Rationale: ",
                                                e.rationale || 'Not recorded'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Claim: ",
                                                e.claimType.replace(/_/g, ' ')
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Stance: ",
                                                e.stance
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Confidence: ",
                                                e.confidence
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Source type: ",
                                                e.sourceKind.replace(/_/g, ' ')
                                            ]
                                        }),
                                        e.inferenceAssumptions && /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Assumptions: ",
                                                e.inferenceAssumptions
                                            ]
                                        }),
                                        e.supersedesEvidenceId && /*#__PURE__*/ _jsxs(SpecraText, {
                                            as: "p",
                                            children: [
                                                "Replaces evidence: ",
                                                e.supersedesEvidenceId
                                            ]
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsxs(SpecraText, {
                                    as: "p",
                                    className: "[overflow-wrap:anywhere]",
                                    children: [
                                        "Evidence ",
                                        id,
                                        " is no longer available. Remove it before saving."
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    "data-testid": Testids.blackHat.removeEvidence,
                                    onClick: ()=>change({
                                            ...draft,
                                            evidenceIds: draft.evidenceIds.filter((item)=>item !== id)
                                        }),
                                    children: "Remove evidence"
                                })
                            ]
                        }, id);
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                role: "alert",
                "data-testid": Testids.blackHat.error,
                children: error
            }),
            saved && /*#__PURE__*/ _jsx("output", {
                "data-testid": Testids.blackHat.saved,
                children: saved
            }),
            draft.conflict && /*#__PURE__*/ _jsxs("div", {
                "data-testid": Testids.blackHat.conflict,
                className: "space-y-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        "data-testid": Testids.blackHat.loadLatest,
                        disabled: busy,
                        onClick: ()=>void loadLatest(),
                        children: "Load latest for comparison"
                    }),
                    loaded && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                className: "[overflow-wrap:anywhere]",
                                children: "Latest saved assessment (read-only). Reconcile your draft above before continuing."
                            }),
                            latest ? /*#__PURE__*/ _jsx(BlackHatFacts, {
                                assessment: latest
                            }) : /*#__PURE__*/ _jsx(SpecraText, {
                                as: "p",
                                className: "[overflow-wrap:anywhere]",
                                children: "No saved assessment remains."
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": Testids.blackHat.reconcile,
                                onClick: ()=>{
                                    change({
                                        ...draft,
                                        version: latest?.version ?? 0,
                                        conflict: false
                                    });
                                    setError('');
                                },
                                children: "I compared and reconciled my draft"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                "data-testid": Testids.blackHat.save,
                disabled: busy || !dirty || draft.conflict || !Object.values(draft.content).some((v)=>v.trim()) || draft.evidenceIds.some((id)=>!evidence.some((e)=>e.id === id)),
                onClick: ()=>void save(),
                children: busy ? 'Saving…' : 'Save Black Hat version'
            })
        ]
    });
}
